import {
	ClinicalTypeEnum,
	ClinicalTypeOptions,
	DisciplineEnum,
	DisciplineOptions,
	LegacyReferralAreaOptions,
	LegacyReferralMessageTypeEnum,
	LegacyReferralMessageTypeOptions,
	LegacyReferralNotificationMessageStatusEnum,
	LegacyReferralNotificationMessageStatusOptions,
	LegacyReferralNotificationStatusOptions,
	LegacyReferralStatusEnum,
	LegacyReferralStatusOptions,
	type AddressViewModel,
	type LegacyReferralMessageViewModel
} from '@generated';
import type { StatusIntents } from '~/theme/variants';

function getLabel(options: { id: number; label: string }[], id: number) {
	return options.find(x => x.id === id)?.label ?? '';
}

export const getReferralStatusLabel = (statusId: number) => getLabel(LegacyReferralStatusOptions, statusId);
export const getAreaLabel = (areaId: number) => getLabel(LegacyReferralAreaOptions, areaId);
export const getNotificationStatusLabel = (statusId: number) => getLabel(LegacyReferralNotificationStatusOptions, statusId);
export const getMessageTypeLabel = (messageTypeId: number) => getLabel(LegacyReferralMessageTypeOptions, messageTypeId);
export const getMessageStatusLabel = (statusId: number) => getLabel(LegacyReferralNotificationMessageStatusOptions, statusId);

export function getReferralStatusIntent(statusId: number): StatusIntents {
	switch (statusId) {
		case LegacyReferralStatusEnum.Accepted:
			return 'success';
		case LegacyReferralStatusEnum.Pending:
			return 'warning';
		default:
			return 'destructive';
	}
}

export function getMessageStatusIntent(statusId: number): StatusIntents {
	switch (statusId) {
		case LegacyReferralNotificationMessageStatusEnum.Sent:
			return 'success';
		case LegacyReferralNotificationMessageStatusEnum.Pending:
			return 'warning';
		case LegacyReferralNotificationMessageStatusEnum.Error:
			return 'destructive';
		default:
			return 'neutral';
	}
}

export function getRoleAbbreviation(roleId: number) {
	return ClinicalTypeOptions.find(x => x.id === roleId)?.abbreviation ?? '';
}

export function getDisciplineAbbreviations(disciplineIds: number[]) {
	return disciplineIds.map(x => DisciplineOptions.find(y => y.id === x)?.abbreviation ?? '');
}

export function getDisciplineIdForRole(roleId: number): number {
	switch (roleId) {
		case ClinicalTypeEnum.RN:
		case ClinicalTypeEnum.LPN:
			return DisciplineEnum.SkilledNursing;
		case ClinicalTypeEnum.PT:
		case ClinicalTypeEnum.PTA:
			return DisciplineEnum.PhysicalTherapy;
		case ClinicalTypeEnum.OT:
		case ClinicalTypeEnum.COTA:
			return DisciplineEnum.OccupationalTherapy;
		case ClinicalTypeEnum.ST:
			return DisciplineEnum.SpeechTherapy;
		case ClinicalTypeEnum.MSW:
			return DisciplineEnum.MedicalSocialWorker;
		case ClinicalTypeEnum.HHA:
			return DisciplineEnum.HomeHealthAide;
		default:
			throw new Error(`Unknown clinical type: ${roleId}`);
	}
}

interface NotificationResponse {
	accepted?: boolean;
	skilledNursingCanStartOnly: boolean;
	skilledNursingCanPrimaryCareOnly: boolean;
}

export function getResponseLabel(notification: NotificationResponse) {
	if (notification.accepted === false) {
		return 'Rejected';
	}
	if (notification.accepted !== true) {
		return 'No Response';
	}
	if (notification.skilledNursingCanStartOnly) {
		return 'Accepted - SOC Only';
	}
	if (notification.skilledNursingCanPrimaryCareOnly) {
		return 'Accepted - Primary Care Only';
	}
	return 'Accepted';
}

export function getResponseIntent(notification: NotificationResponse): StatusIntents {
	if (notification.accepted === false) {
		return 'destructive';
	}
	return notification.accepted === true ? 'success' : 'neutral';
}

export function getNewReferralMessage(messages: LegacyReferralMessageViewModel[], userId: string) {
	return messages
		.filter(x => x.userId === userId && x.messageTypeId === LegacyReferralMessageTypeEnum.NewReferral)
		.sort((x, y) => new Date(y.dateCreated).getTime() - new Date(x.dateCreated).getTime())[0];
}

export function formatAddress(address: AddressViewModel) {
	return [address.addressOne, address.city, `${address.state} ${address.zipCode}`].filter(Boolean).join(', ');
}
